

import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  BadgeDollarSign,
  Banknote,
  Grid2X2,
  Layers3,
  Menu,
  ReceiptText,
  ShieldCheck,
  ShoppingCart,
  User,
} from "lucide-react";

import Sidebar from "../../components/Sidebar";
import AdminProducts from "./AdminProducts";
import AdminOrders from "./AdminOrders";
import AdminSettings from "./AdminSettings";
import AdminAnalysis from "./AdminAnalysis";
import AdminRepairManager from "./AdminRepairsManager";
import AdminParts from "./AdminParts";
import AdminSales from "./AdminSales";
import AdminDevicePurchases from "./AdminDevicePurchases";
import AdminExpensesManager from "./AdminExpensesManager";
import AdminSuppliers from "./AdminSuppliers";
import AdminCustomers from "./AdminCustomers";

import { API_BASE_URL } from "../../config/api";

/*
|--------------------------------------------------------------------------
| Emërtimet e faqeve
|--------------------------------------------------------------------------
*/

const pageLabels = {
  dashboard: "Dashboardi",
  products: "Produkte",
  suppliers: "Furnitorët",
  customers: "Klientët",
  "device-purchases": "Blerje Pajisjesh",
  parts: "Pjesë",
  repairs: "Riparime",
  expenses: "Shpenzimet e dyqanit",
  sales: "Shitje",
  orders: "Porosi",
  settings: "Konfigurime",
  analysis: "Analiza",
};


const formatPrice = (value) =>
  new Intl.NumberFormat("sq-AL", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

/*
|--------------------------------------------------------------------------
| Komponenti që përshtat faqet e adminit brenda UserDashboard
|--------------------------------------------------------------------------
|
| AdminProducts, AdminParts, AdminOrders dhe AdminRepairs kanë Sidebar,
| hamburger dhe main layout të tyre.
|
| Këto klasa:
| - fshehin Sidebar-in e brendshëm;
| - fshehin hamburger-in e brendshëm;
| - heqin margin-left e adminit;
| - lejojnë tabelën të zërë gjithë gjerësinë;
| - mbajnë horizontal scroll brenda tabelës.
|
|--------------------------------------------------------------------------
*/

const EmbeddedAdminPage = ({ children }) => {
  return (
    <div
      className="
        w-full
        min-w-0
        max-w-full
        overflow-x-hidden

        [&>div]:!w-full
        [&>div]:!min-w-0
        [&>div]:!max-w-none
        [&>div]:!min-h-0
        [&>div]:!overflow-x-hidden
        [&>div]:!bg-transparent

        [&>div>aside]:!hidden
        [&>div>button]:!hidden
        [&>div>div.fixed]:!hidden

        [&>div>main]:!ml-0
        [&>div>main]:!w-full
        [&>div>main]:!min-w-0
        [&>div>main]:!max-w-none
        [&>div>main]:!min-h-0
        [&>div>main]:!overflow-x-hidden
        [&>div>main]:!p-0

        [&>div>main>div]:!w-full
        [&>div>main>div]:!min-w-0
        [&>div>main>div]:!max-w-full

        [&_.overflow-x-auto]:!w-full
        [&_.overflow-x-auto]:!max-w-full
        [&_.overflow-x-auto]:!overflow-x-auto
      "
    >
      {children}
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| UserDashboard
|--------------------------------------------------------------------------
*/

const UserDashboard = () => {
  const storedUser = useMemo(() => {
    try {
      const value = localStorage.getItem("user");
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error("Invalid user data in localStorage:", error);
      return null;
    }
  }, []);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [activePage, setActivePage] = useState("dashboard");

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  const [salesBonus, setSalesBonus] = useState(0);

  const [repairStats, setRepairStats] = useState({
    repairsCount: 0,
    readyCount: 0,
    repairProfit: 0,
  });

  const [repairStatsError, setRepairStatsError] = useState("");
  const [repairDateFrom, setRepairDateFrom] = useState("");
  const [repairDateTo, setRepairDateTo] = useState("");

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [form, setForm] = useState({
    username: "",
    password: "",
  });

  /*
  |--------------------------------------------------------------------------
  | Merr profilin dhe permissions
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        setProfileError("");

        const token = localStorage.getItem("authToken");

        if (!storedUser?.id || !token) {
          window.location.href = "/login";
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/users/${storedUser.id}/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message || "Nuk u arrit të merrej profili."
          );
        }

        const safePermissions = Array.isArray(data.permissions)
          ? data.permissions
          : [];

        setProfile(data.user || storedUser);
        setPermissions(safePermissions);

        setForm({
          username:
            data.user?.username || storedUser?.username || "",
          password: "",
        });
      } catch (error) {
        console.error("Profile fetch error:", error);

        setProfileError(
          error.message || "Nuk u arrit të ngarkohej profili."
        );
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [storedUser?.id]);

  /*
  |--------------------------------------------------------------------------
  | Bonusi i përdoruesit nga Products
  |--------------------------------------------------------------------------
  |
  | /api/stats kthen bonusin sipas:
  | (Products.price - Products.purchase_price)
  | * Products.bonus_share_percent / 100
  | * sasia e shitur
  |
  | Këtu marrim vetëm rreshtin e përdoruesit të loguar.
  */

  useEffect(() => {
    const fetchSalesBonus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/stats?range=all`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
            data?.error ||
            "Nuk u arrit të merrej bonusi."
          );
        }

        const rows = Array.isArray(data.salesByUser)
          ? data.salesByUser
          : [];

        const currentUserSales = rows.find(
          (item) =>
            String(item.seller_id) ===
            String(storedUser?.id)
        );

        setSalesBonus(
          Number(currentUserSales?.bonus || 0)
        );
      } catch (error) {
        console.error(
          "Sales bonus fetch error:",
          error
        );

        setSalesBonus(0);
      }
    };

    if (storedUser?.id) {
      fetchSalesBonus();
    }
  }, [storedUser?.id]);

  /*
  |--------------------------------------------------------------------------
  | Statistikat e riparimeve
  |--------------------------------------------------------------------------
  |
  | - "Riparimet e mia" = të gjitha serviset në queue.
  | - Fitimi i user-it merret nga historiku:
  |   çdo servis ku user-i aktual e ka kaluar statusin në "ready".
  | - Formula për çdo servis:
  |   50% × (service_price - repair_cost)
  | - Filtri i kalendarit zbatohet mbi datën e hyrjes "ready" në historik.
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchRepairStats = async () => {
      const token = localStorage.getItem("authToken");
      const currentUserId = storedUser?.id;

      if (!token || !currentUserId) {
        setRepairStats({
          repairsCount: 0,
          readyCount: 0,
          repairProfit: 0,
        });
        return;
      }

      const isInsideSelectedDateRange = (dateValue) => {
        if (!dateValue) return false;

        const value = new Date(dateValue);
        if (Number.isNaN(value.getTime())) return false;

        if (repairDateFrom) {
          const from = new Date(`${repairDateFrom}T00:00:00`);
          if (value < from) return false;
        }

        if (repairDateTo) {
          const to = new Date(`${repairDateTo}T23:59:59.999`);
          if (value > to) return false;
        }

        return true;
      };

      try {
        setRepairStatsError("");
        const repairsResponse = await fetch(
          `${API_BASE_URL}/api/repairs`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const repairsData = await repairsResponse.json();

        if (!repairsResponse.ok) {
          throw new Error(
            repairsData?.message ||
              repairsData?.error ||
              "Nuk u arrit të merren riparimet."
          );
        }

        const repairs = Array.isArray(repairsData)
          ? repairsData
          : repairsData?.repairs || repairsData?.data || [];

        if (!Array.isArray(repairs)) {
          throw new Error("API /api/repairs nuk ktheu një listë riparimesh.");
        }
        if (repairs.length && !repairs.some((repair) =>
          Object.prototype.hasOwnProperty.call(repair, "completed_by_user_id")
        )) {
          throw new Error("API /api/repairs nuk kthen completed_by_user_id. Kontrollo backend-in dhe deploy-in.");
        }

        /*
         * Queue = të gjitha serviset që janë ende pjesë e rrjedhës së punës.
         * Përjashtojmë vetëm "delivered" dhe "cancelled".
         */
        const queueRepairs = repairs.filter((repair) => {
          const status = String(repair?.status || "").toLowerCase();
          return !["delivered", "cancelled"].includes(status);
        });

        /*
         * Fitimi i përdoruesit merret nga completed_by_user_id.
         * Numërohen vetëm serviset ready/delivered, një herë secili.
         * Për filtrin e datës preferohet completed_at, pastaj updated_at,
         * dhe në mungesë të tyre created_at.
         */
   const myCompletedRepairs = repairs.filter((repair) => {
  const status = String(repair?.status || "")
    .trim()
    .toLowerCase();

      const belongsToCurrentUser =
        String(repair?.completed_by_user_id ?? "") ===
        String(currentUserId);

      if (
        !belongsToCurrentUser ||
        !["ready", "delivered"].includes(status)
      ) {
        return false;
      }

  const readyDate =
    repair?.ready_date ||
    repair?.completed_at ||
    repair?.updated_at ||
    repair?.created_at;

  return isInsideSelectedDateRange(readyDate);
});

const repairProfit = myCompletedRepairs.reduce(
  (total, repair) => {
    const price = Number(repair?.service_price || 0);

    return total + price * 0.5;
  },
  0
);

      

        setRepairStats({
          repairsCount: queueRepairs.length,
          readyCount: myCompletedRepairs.length,
          repairProfit,
        });
      } catch (error) {
        console.error("Repair stats fetch error:", error);
        setRepairStatsError(error.message || "Nuk u ngarkuan statistikat e riparimeve.");

        setRepairStats({
          repairsCount: 0,
          readyCount: 0,
          repairProfit: 0,
        });
      }
    };

    if (
      storedUser?.id &&
      permissions.includes("repairs")
    ) {
      fetchRepairStats();
    } else {
      setRepairStatsError("");
      setRepairStats({
        repairsCount: 0,
        readyCount: 0,
        repairProfit: 0,
      });
    }
  }, [
    storedUser?.id,
    permissions,
    repairDateFrom,
    repairDateTo,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Kontrolli i aksesit
  |--------------------------------------------------------------------------
  */

  const canAccess = (page) => {
    if (page === "dashboard") return true;

    return permissions.includes(page);
  };

  const handlePageChange = (page) => {
    if (!canAccess(page)) {
      setActivePage("dashboard");
      setSidebarOpen(false);
      return;
    }

    setActivePage(page);
    setSidebarOpen(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
  |--------------------------------------------------------------------------
  | Përditësimi i profilit
  |--------------------------------------------------------------------------
  */

  const updateProfile = async () => {
    const cleanUsername = form.username.trim();

    if (!cleanUsername) {
      alert("Username është i detyrueshëm.");
      return;
    }

    try {
      setSavingProfile(true);

      const token = localStorage.getItem("authToken");

      const response = await fetch(
        `${API_BASE_URL}/api/users/${storedUser.id}/profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            username: cleanUsername,
            password: form.password,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message || "Përditësimi i profilit dështoi."
        );
      }

      const updatedUser = {
        ...storedUser,
        username: cleanUsername,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      setProfile((previousProfile) => ({
        ...previousProfile,
        username: cleanUsername,
      }));

      setForm({
        username: cleanUsername,
        password: "",
      });

      setProfileModalOpen(false);

      alert("Profili u përditësua me sukses.");
    } catch (error) {
      console.error("Profile update error:", error);

      alert(
        error.message || "Përditësimi i profilit dështoi."
      );
    } finally {
      setSavingProfile(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Dashboard kryesor
  |--------------------------------------------------------------------------
  */

  const renderDashboardHome = () => {
    const availableModules = permissions.filter(
      (permission) =>
        permission !== "dashboard" &&
        Object.prototype.hasOwnProperty.call(
          pageLabels,
          permission
        )
    );

    const hasSalesPermission =
      permissions.includes("sales");

    const hasRepairsPermission =
      permissions.includes("repairs");

    return (
      <div className="w-full min-w-0 max-w-none space-y-4 sm:space-y-5 lg:space-y-6">
        {/* Welcome */}
        <section
          className="
              relative
              w-full
              overflow-hidden
              rounded-3xl
              bg-gradient-to-br
              from-blue-700
              via-indigo-700
              to-violet-700
              px-4
              py-7
              text-white
              shadow-xl

              sm:px-7
              sm:py-9

              lg:px-6
              lg:py-8
            "
          >
          <div
            className="
              absolute
              -right-20
              -top-20
              h-72
              w-72
              rounded-full
              bg-white/10
              blur-3xl
            "
          />

          <div
            className="
              absolute
              -bottom-24
              left-1/3
              h-64
              w-64
              rounded-full
              bg-cyan-300/10
              blur-3xl
            "
          />

          <div className="relative z-10 max-w-3xl">
            <span
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-white/20
                bg-white/10
                px-3
                py-1.5
                text-xs
                font-semibold
                backdrop-blur-sm
              "
            >
              <Activity size={15} />
              Llogaria aktive
            </span>

            <h1
              className="
                mt-5
                text-3xl
                font-bold
                tracking-tight
                sm:text-4xl
              "
            >
              Mirë se erdhe,{" "}
              {profile?.username ||
                storedUser?.username ||
                "Përdorues"}
            </h1>

            <p
              className="
                mt-3
                max-w-2xl
                text-sm
                leading-6
                text-blue-100
                sm:text-base
              "
            >
              Menaxho produktet, pjesët, porositë dhe
              riparimet që të janë autorizuar nga
              administratori.
            </p>

            {hasSalesPermission && (
              <div className="mt-5 flex flex-wrap gap-3">
                <div
                  className="
                    inline-flex
                    items-center
                    gap-2
                    rounded-2xl
                    border
                    border-white/20
                    bg-white/10
                    px-4
                    py-2.5
                    backdrop-blur-sm
                  "
                >
                  <BadgeDollarSign size={18} />

                  <span className="text-sm text-blue-100">
                    Bonusi:
                  </span>

                  <strong className="text-base text-white">
                    {formatPrice(
                      salesBonus
                    )}{" "}
                    ALL
                  </strong>
                </div>

                <div
                  className="
                    inline-flex
                    items-center
                    gap-2
                    rounded-2xl
                    border
                    border-white/20
                    bg-white/10
                    px-4
                    py-2.5
                    backdrop-blur-sm
                  "
                >
                  <ShoppingCart size={18} />

                  <span className="text-sm text-blue-100">
                    Shitje:
                  </span>

                  <strong className="text-base text-white">
                    {Number(
                      profile?.sales_count || 0
                    )}
                  </strong>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="
                mt-6
                inline-flex
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-white
                px-5
                py-2.5
                text-sm
                font-semibold
                text-blue-700
                shadow-lg
                transition
                hover:-translate-y-0.5
                hover:bg-blue-50
                active:translate-y-0
              "
            >
              <User size={18} />
              Modifiko profilin
            </button>
          </div>
        </section>

        {hasRepairsPermission && (
          <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  Filtro fitimin e riparimeve
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Filtri përdor datën Gati, ose datën e përfundimit/përditësimit kur ajo mungon.
                </p>
              </div>

              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">
                    Nga data
                  </label>
                  <input
                    type="date"
                    lang="en-GB"
                    value={repairDateFrom}
                    onChange={(event) => setRepairDateFrom(event.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">
                    Deri më
                  </label>
                  <input
                    type="date"
                    lang="en-GB"
                    value={repairDateTo}
                    onChange={(event) => setRepairDateTo(event.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
            </div>

            {(repairDateFrom || repairDateTo) && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setRepairDateFrom("");
                    setRepairDateTo("");
                  }}
                  className="rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
                >
                  Pastro filtrin
                </button>
              </div>
            )}
            {repairStatsError && (
              <p role="alert" className="mt-3 text-sm font-semibold text-red-600">
                {repairStatsError}
              </p>
            )}
          </section>
        )}

        {/* Statistics */}
        <section
          className="
            grid
            grid-cols-1
            gap-4
            sm:grid-cols-2
            xl:grid-cols-4
          "
        >
          <article
            className="
              rounded-2xl
              border
              border-gray-200
              bg-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500">
                  Përdoruesi
                </p>

                <p
                  className="
                    mt-2
                    truncate
                    text-xl
                    font-bold
                    text-gray-900
                  "
                >
                  {profile?.username ||
                    storedUser?.username ||
                    "-"}
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-blue-50
                  text-blue-600
                "
              >
                <User size={24} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Roli:{" "}
              <span className="font-semibold capitalize text-gray-700">
                {profile?.role ||
                  storedUser?.role ||
                  "user"}
              </span>
            </p>
          </article>

          <article
            className="
              rounded-2xl
              border
              border-gray-200
              bg-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Statusi
                </p>

                <p className="mt-2 text-xl font-bold text-green-600">
                  Aktiv
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-green-50
                  text-green-600
                "
              >
                <ShieldCheck size={25} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Llogaria është aktive dhe funksionale.
            </p>
          </article>

          <article
            className="
              rounded-2xl
              border
              border-gray-200
              bg-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
              sm:col-span-2
              xl:col-span-1
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Module të lejuara
                </p>

                <p className="mt-2 text-xl font-bold text-violet-600">
                  {availableModules.length}
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-violet-50
                  text-violet-600
                "
              >
                <Layers3 size={25} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Module të autorizuara nga administratori.
            </p>
          </article>

          {hasSalesPermission && (
            <>
          {/* BONUSI TOTAL */}
          <article
            className="
              rounded-2xl
              border
              border-emerald-200
              bg-gradient-to-br
              from-emerald-50
              to-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500">
                  Bonusi im
                </p>

                <p className="mt-2 text-2xl font-extrabold text-emerald-700">
                  {formatPrice(
                    salesBonus
                  )}{" "}
                  ALL
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-emerald-100
                  text-emerald-700
                "
              >
                <BadgeDollarSign size={25} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Bonusi llogaritet sipas fitimit dhe përqindjes së përcaktuar te produktet.
            </p>
          </article>

          {/* NUMRI I SHITJEVE */}
          <article
            className="
              rounded-2xl
              border
              border-sky-200
              bg-gradient-to-br
              from-sky-50
              to-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Shitjet e mia
                </p>

                <p className="mt-2 text-2xl font-extrabold text-sky-700">
                  {Number(
                    profile?.sales_count || 0
                  )}
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-sky-100
                  text-sky-700
                "
              >
                <ShoppingCart size={24} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Numri total i shitjeve të regjistruara nga llogaria jote.
            </p>
          </article>

          {/* XHIRO TOTAL */}
          <article
            className="
              rounded-2xl
              border
              border-amber-200
              bg-gradient-to-br
              from-amber-50
              to-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500">
                  Xhiroja ime
                </p>

                <p className="mt-2 text-2xl font-extrabold text-amber-700">
                  {formatPrice(
                    profile?.sales_total || 0
                  )}{" "}
                  ALL
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-amber-100
                  text-amber-700
                "
              >
                <Banknote size={24} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Vlera totale e shitjeve të regjistruara prej teje.
            </p>
          </article>

          {/* MESATARJA E SHITJES */}
          <article
            className="
              rounded-2xl
              border
              border-violet-200
              bg-gradient-to-br
              from-violet-50
              to-white
              p-5
              shadow-sm
              transition
              hover:-translate-y-1
              hover:shadow-lg
            "
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500">
                  Mesatarja / shitje
                </p>

                <p className="mt-2 text-2xl font-extrabold text-violet-700">
                  {formatPrice(
                    profile?.average_sale || 0
                  )}{" "}
                  ALL
                </p>
              </div>

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-violet-100
                  text-violet-700
                "
              >
                <ReceiptText size={24} />
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Vlera mesatare e një shitjeje të regjistruar nga ti.
            </p>
          </article>
            </>
          )}

          {hasRepairsPermission && (
            <>
              {/* NUMRI I RIPARIMEVE */}
              <article
                className="
                  rounded-2xl
                  border
                  border-indigo-200
                  bg-gradient-to-br
                  from-indigo-50
                  to-white
                  p-5
                  shadow-sm
                  transition
                  hover:-translate-y-1
                  hover:shadow-lg
                "
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500">
                      Riparimet e mia
                    </p>

                    <p className="mt-2 text-2xl font-extrabold text-indigo-700">
                      {repairStats.repairsCount}
                    </p>
                  </div>

                  <div
                    className="
                      flex
                      h-12
                      w-12
                      shrink-0
                      items-center
                      justify-center
                      rounded-2xl
                      bg-indigo-100
                      text-indigo-700
                    "
                  >
                    <Activity size={24} />
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-500">
                  Të gjitha serviset aktive në queue.
                  {" "}
                  <strong className="text-gray-700">
                    {repairStats.readyCount} të përfshira në fitim
                  </strong>
                </p>

                <button
                  type="button"
                  onClick={() => handlePageChange("repairs")}
                  className="
                    mt-4
                    inline-flex
                    items-center
                    gap-2
                    rounded-xl
                    bg-indigo-600
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    text-white
                    transition
                    hover:bg-indigo-700
                  "
                >
                  Hap riparimet
                </button>
              </article>

              {/* FITIMI NGA RIPARIMET GATI */}
              <article
                className="
                  rounded-2xl
                  border
                  border-emerald-200
                  bg-gradient-to-br
                  from-emerald-50
                  to-white
                  p-5
                  shadow-sm
                  transition
                  hover:-translate-y-1
                  hover:shadow-lg
                "
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500">
                      Fitimi nga riparimet
                    </p>

                    <p className="mt-2 text-2xl font-extrabold text-emerald-700">
                      {formatPrice(repairStats.repairProfit)} ALL
                    </p>
                  </div>

                  <div
                    className="
                      flex
                      h-12
                      w-12
                      shrink-0
                      items-center
                      justify-center
                      rounded-2xl
                      bg-emerald-100
                      text-emerald-700
                    "
                  >
                    <BadgeDollarSign size={25} />
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-500">
                  Nga serviset e përfunduara prej teje me status
                  {" "}
                  <strong className="text-emerald-700">
                    Gati / Dorëzuar
                  </strong>
                  . Fitimi = 50% × (Totali − Kosto riparimi).
                </p>
              </article>
            </>
          )}

        </section>

        {/* Modules */}
        <section
          className="
            rounded-3xl
            border
            border-gray-200
            bg-white
            p-4
            shadow-sm
            sm:p-6
          "
        >
          <div className="mb-5 flex items-start gap-3">
            <div
              className="
                flex
                h-11
                w-11
                shrink-0
                items-center
                justify-center
                rounded-2xl
                bg-blue-50
                text-blue-600
              "
            >
              <Grid2X2 size={22} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Modulet e autorizuara
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Zgjidh një modul për të vazhduar.
              </p>
            </div>
          </div>

          {availableModules.length > 0 ? (
            <div
              className="
                grid
                grid-cols-1
                gap-3
                sm:grid-cols-2
                xl:grid-cols-3
              "
            >
              {availableModules.map((module) => (
                <button
                  key={module}
                  type="button"
                  onClick={() => handlePageChange(module)}
                  className="
                    group
                    flex
                    min-h-[88px]
                    items-center
                    justify-between
                    gap-4
                    rounded-2xl
                    border
                    border-gray-200
                    bg-gray-50
                    px-4
                    py-4
                    text-left
                    transition
                    hover:-translate-y-0.5
                    hover:border-blue-300
                    hover:bg-blue-50
                    hover:shadow-md
                  "
                >
                  <div className="min-w-0">
                    <p
                      className="
                        truncate
                        font-semibold
                        text-gray-800
                        transition
                        group-hover:text-blue-700
                      "
                    >
                      {pageLabels[module] || module}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      Hap modulin
                    </p>
                  </div>

                  <span
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-white
                      text-blue-600
                      shadow-sm
                      transition
                      group-hover:translate-x-1
                    "
                  >
                    →
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div
              className="
                rounded-2xl
                border
                border-dashed
                border-gray-300
                bg-gray-50
                px-5
                py-10
                text-center
              "
            >
              <p className="font-semibold text-gray-700">
                Nuk ke module të autorizuara.
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Kontakto administratorin për të marrë akses.
              </p>
            </div>
          )}
        </section>
      </div>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | No access
  |--------------------------------------------------------------------------
  */

  const renderNoAccess = () => {
    return (
      <div
        className="
          mx-auto
          max-w-xl
          rounded-3xl
          border
          border-red-200
          bg-red-50
          px-6
          py-12
          text-center
          shadow-sm
        "
      >
        <h2 className="text-xl font-bold text-red-700">
          Nuk ke akses
        </h2>

        <p className="mt-2 text-sm text-red-600">
          Administratori nuk të ka dhënë permission për këtë
          modul.
        </p>

        <button
          type="button"
          onClick={() => setActivePage("dashboard")}
          className="
            mt-6
            inline-flex
            items-center
            gap-2
            rounded-xl
            bg-red-600
            px-5
            py-2.5
            text-sm
            font-semibold
            text-white
            transition
            hover:bg-red-700
          "
        >
          <ArrowLeft size={17} />
          Kthehu te Dashboard
        </button>
      </div>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Renderimi i modulit aktiv
  |--------------------------------------------------------------------------
  */

const renderPage = () => {
  if (!canAccess(activePage)) {
    return renderNoAccess();
  }

  switch (activePage) {
    case "dashboard":
      return renderDashboardHome();

    case "products":
  return (
    <EmbeddedAdminPage>
      <AdminProducts />
    </EmbeddedAdminPage>
  );

  case "suppliers":
  return (
    <EmbeddedAdminPage>
      <AdminSuppliers />
    </EmbeddedAdminPage>
  );

case "customers":
  return (
    <EmbeddedAdminPage>
      <AdminCustomers />
    </EmbeddedAdminPage>
  );

case "device-purchases":
  return (
    <EmbeddedAdminPage>
      <AdminDevicePurchases />
    </EmbeddedAdminPage>
  );

case "expenses":
  return (
    <EmbeddedAdminPage>
      <AdminExpensesManager />
    </EmbeddedAdminPage>
  );

case "sales":
  return (
    <EmbeddedAdminPage>
      <AdminSales />
    </EmbeddedAdminPage>
  );

case "parts":
  return (
    <EmbeddedAdminPage>
      <AdminParts />
    </EmbeddedAdminPage>
  );
    case "orders":
      return (
        <EmbeddedAdminPage>
          <AdminOrders />
        </EmbeddedAdminPage>
      );

    case "repairs":
      return (
        <EmbeddedAdminPage>
          <AdminRepairManager />
        </EmbeddedAdminPage>
      );

      case "expenses":
      return (
        <EmbeddedAdminPage>
          <AdminExpensesManager />
        </EmbeddedAdminPage>
      );

    case "analysis":
      return (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-10 text-center shadow-sm sm:px-5 sm:py-12">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
            Analiza
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Moduli i analizës do të shfaqet këtu.
          </p>
        </div>
      );

    default:
      return renderDashboardHome();
  }
};

  /*
  |--------------------------------------------------------------------------
  | Authentication guard
  |--------------------------------------------------------------------------
  */

  if (!storedUser) {
    return (
      <div
        className="
          flex
          min-h-screen
          items-center
          justify-center
          bg-gray-100
          px-4
        "
      >
        <div
          className="
            w-full
            max-w-md
            rounded-3xl
            border
            border-gray-200
            bg-white
            p-8
            text-center
            shadow-xl
          "
        >
          <h1 className="text-xl font-bold text-gray-900">
            Duhet të identifikohesh
          </h1>

          <p className="mt-2 text-sm text-gray-500">
            Sesioni nuk është aktiv.
          </p>

          <button
            type="button"
            onClick={() => {
              window.location.href = "/login";
            }}
            className="
              mt-6
              rounded-xl
              bg-blue-600
              px-5
              py-2.5
              font-semibold
              text-white
              transition
              hover:bg-blue-700
            "
          >
            Shko te Login
          </button>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Main layout
  |--------------------------------------------------------------------------
  */

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      <Sidebar
        setActivePage={handlePageChange}
        permissions={permissions}
        activePage={activePage}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Hamburger mobile */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="
          fixed
          left-4
          top-4
          z-30
          flex
          h-11
          w-11
          items-center
          justify-center
          rounded-xl
          bg-gradient-to-br
          from-blue-600
          to-indigo-700
          text-white
          shadow-lg
          transition
          hover:scale-105
          active:scale-95
          lg:hidden
        "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>
            <main
        className="
          min-h-screen
          w-full
          min-w-0
          overflow-x-hidden

          px-0
          pb-4
          pt-20

          lg:ml-64
          lg:w-[calc(100%-16rem)]
          lg:max-w-none
          lg:px-4
          lg:pb-4
          lg:pt-4

          xl:px-5
        "
      >
{/* Header i modulit */}
        {activePage !== "dashboard" && (
          <div
            className="
              mb-4
              flex
              w-full
              min-w-0
              flex-col
              gap-2
              sm:flex-row
              sm:items-center
              sm:justify-between
            "
          >
            <div className="min-w-0">
              <p
                className="
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.18em]
                  text-blue-600
                "
              >
                User Panel
              </p>

            
            </div>

            <button
              type="button"
              onClick={() => handlePageChange("dashboard")}
              className="
                inline-flex
                w-full
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-gray-300
                bg-white
                px-3.5
                py-2
                text-sm
                font-semibold
                text-gray-700
                shadow-sm
                transition
                hover:border-blue-300
                hover:bg-blue-50
                hover:text-blue-700
                sm:w-auto
              "
            >
              <ArrowLeft size={17} />
              Kthehu te Dashboard
            </button>
          </div>
        )}

        {profileLoading ? (
          <div
            className="
              flex
              min-h-[400px]
              items-center
              justify-center
            "
          >
            <div className="text-center">
              <div
                className="
                  mx-auto
                  h-11
                  w-11
                  animate-spin
                  rounded-full
                  border-4
                  border-blue-100
                  border-t-blue-600
                "
              />

              <p className="mt-4 text-sm text-gray-500">
                Duke ngarkuar profilin...
              </p>
            </div>
          </div>
        ) : profileError ? (
          <div
            className="
              mx-auto
              max-w-xl
              rounded-3xl
              border
              border-red-200
              bg-red-50
              p-6
              text-center
            "
          >
            <h2 className="font-bold text-red-700">
              Ndodhi një problem
            </h2>

            <p className="mt-2 text-sm text-red-600">
              {profileError}
            </p>
          </div>
        ) : (
          <div className="w-full min-w-0 max-w-full">
            {renderPage()}
          </div>
        )}
      </main>

      {/* Modal i profilit */}
      {profileModalOpen && (
        <>
          <div
            className="
              fixed
              inset-0
              z-[9998]
              bg-black/55
              backdrop-blur-sm
            "
            onClick={() => setProfileModalOpen(false)}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-profile-title"
            className="
              fixed
              left-1/2
              top-1/2
              z-[9999]
              w-[calc(100%-1.5rem)]
              max-w-md
              -translate-x-1/2
              -translate-y-1/2
              overflow-hidden
              rounded-3xl
              bg-white
              shadow-2xl
            "
          >
            <div
              className="
                bg-gradient-to-r
                from-blue-700
                to-indigo-700
                px-5
                py-5
                text-white
              "
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2
                    id="edit-profile-title"
                    className="text-xl font-bold"
                  >
                    Modifiko profilin
                  </h2>

                  <p className="mt-1 text-sm text-blue-100">
                    Përditëso username ose password.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setProfileModalOpen(false)}
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-white/10
                    text-xl
                    transition
                    hover:bg-white/20
                  "
                  aria-label="Mbyll modalin"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="space-y-5 p-5 sm:p-6">
              <div>
                <label
                  htmlFor="profileUsername"
                  className="
                    mb-1.5
                    block
                    text-sm
                    font-medium
                    text-gray-700
                  "
                >
                  Username
                </label>

                <input
                  id="profileUsername"
                  type="text"
                  value={form.username}
                  onChange={(event) =>
                    setForm((previousForm) => ({
                      ...previousForm,
                      username: event.target.value,
                    }))
                  }
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-3.5
                    py-3
                    outline-none
                    transition
                    focus:border-blue-500
                    focus:ring-4
                    focus:ring-blue-100
                  "
                />
              </div>

              <div>
                <label
                  htmlFor="profilePassword"
                  className="
                    mb-1.5
                    block
                    text-sm
                    font-medium
                    text-gray-700
                  "
                >
                  Password i ri
                </label>

                <input
                  id="profilePassword"
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    setForm((previousForm) => ({
                      ...previousForm,
                      password: event.target.value,
                    }))
                  }
                  placeholder="Lëre bosh për të mos e ndryshuar"
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-3.5
                    py-3
                    outline-none
                    transition
                    focus:border-blue-500
                    focus:ring-4
                    focus:ring-blue-100
                  "
                />
              </div>
            </div>

            <div
              className="
                grid
                grid-cols-1
                gap-3
                border-t
                border-gray-200
                bg-gray-50
                p-4
                sm:grid-cols-2
                sm:px-6
              "
            >
              <button
                type="button"
                onClick={() => setProfileModalOpen(false)}
                disabled={savingProfile}
                className="
                  order-2
                  rounded-xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  py-2.5
                  font-semibold
                  text-gray-700
                  transition
                  hover:bg-gray-100
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  sm:order-1
                "
              >
                Anulo
              </button>

              <button
                type="button"
                onClick={updateProfile}
                disabled={savingProfile}
                className="
                  order-1
                  rounded-xl
                  bg-gradient-to-r
                  from-blue-600
                  to-indigo-700
                  px-4
                  py-2.5
                  font-semibold
                  text-white
                  shadow-md
                  transition
                  hover:shadow-lg
                  active:scale-[0.98]
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  sm:order-2
                "
              >
                {savingProfile
                  ? "Duke ruajtur..."
                  : "Ruaj ndryshimet"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDashboard;